// Attaches Bazaarvoice's ratings and reviews to the theme's Product JSON-LD (PDP only).
// BV injects its own blocks into <head>, but untyped: one carries an aggregateRating and only an
// @id, the other a bare review array. Neither declares @type, so Google has no Product to hang
// them off and Search Console reports the product as having no aggregateRating. This copies both
// onto our Product node so the page exposes one complete, properly typed entity.
// The two BV blocks land at different moments, so rating and reviews are tracked separately and
// the node is rewritten as each arrives.
(function () {
  'use strict';

  // BV can take several seconds, and this page mutates constantly, so bound the watch by wall
  // clock rather than by tick count: a tick budget is exhausted by unrelated DOM churn long
  // before BV's second block lands.
  var DEADLINE_MS = 30000;

  var ratingApplied = false;
  var reviewsApplied = false;
  var serverRendered = null; // Decided on the first look at our own node.
  var deadline = 0;
  var observer = null;

  // Parsed contents of every JSON-LD block on the page, with a handle back to the script tag.
  function readBlocks() {
    var scripts = document.querySelectorAll('script[type="application/ld+json"]');
    var blocks = [];
    for (var i = 0; i < scripts.length; i++) {
      try {
        blocks.push({ script: scripts[i], data: JSON.parse(scripts[i].textContent) });
      } catch (e) {
        continue; // Not ours to repair; skip anything unparseable.
      }
    }
    return blocks;
  }

  // Our own node, rendered by snippets/stmt-schema.liquid.
  function findProduct(blocks) {
    for (var i = 0; i < blocks.length; i++) {
      var data = blocks[i].data;
      if (data && !Array.isArray(data) && data['@type'] === 'Product') {
        return blocks[i];
      }
    }
    return null;
  }

  // BV's payloads, matched on shape rather than @id: our @id comes from canonical_url, which can
  // carry ?variant=, while BV always uses the clean product URL.
  function findBvPayload(blocks, product) {
    var payload = { aggregateRating: null, review: null };
    for (var i = 0; i < blocks.length; i++) {
      if (blocks[i] === product) {
        continue;
      }
      var data = blocks[i].data;
      if (!data || Array.isArray(data) || data['@type']) {
        continue; // BV's blocks are the untyped ones.
      }
      if (!payload.aggregateRating && data.aggregateRating) {
        payload.aggregateRating = data.aggregateRating;
      }
      if (!payload.review && Array.isArray(data.review) && data.review.length) {
        payload.review = data.review;
      }
    }
    return payload;
  }

  // BV omits @type on the nested nodes, so add what schema.org needs to read them as reviews.
  function normaliseReviews(reviews) {
    var out = [];
    for (var i = 0; i < reviews.length; i++) {
      var review = reviews[i];
      if (!review || !review.reviewRating || !review.author) {
        continue; // Google rejects a review without both; drop rather than emit a partial.
      }
      review['@type'] = 'Review';
      out.push(review);
    }
    return out;
  }

  function settled() {
    return (ratingApplied && reviewsApplied) || Date.now() > deadline;
  }

  function apply() {
    var blocks = readBlocks();
    var product = findProduct(blocks);
    if (!product) {
      return; // Not a product page, or our node has not rendered.
    }

    // If a rating is ever rendered server-side, it wins and we stay out of the way.
    if (serverRendered === null) {
      serverRendered = !!product.data.aggregateRating;
    }
    if (serverRendered) {
      ratingApplied = true;
      reviewsApplied = true;
      return;
    }

    var payload = findBvPayload(blocks, product);
    var changed = false;

    if (!ratingApplied && payload.aggregateRating) {
      var rating = payload.aggregateRating;
      // An aggregateRating with no reviews behind it is a rich-results violation.
      if (rating.ratingValue && Number(rating.reviewCount)) {
        rating['@type'] = 'AggregateRating';
        product.data.aggregateRating = rating;
        ratingApplied = true;
        changed = true;
      }
    }

    if (!reviewsApplied && payload.review) {
      var reviews = normaliseReviews(payload.review);
      if (reviews.length) {
        product.data.review = reviews;
        reviewsApplied = true;
        changed = true;
      }
    }

    if (changed) {
      product.script.textContent = JSON.stringify(product.data);
    }
  }

  var scheduled = false;
  function scheduleApply() {
    if (scheduled) {
      return;
    }
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      apply();
      // Checked after the deferred run, so the decision uses post-apply state.
      if (observer && settled()) {
        observer.disconnect();
      }
    }, 100);
  }

  function init() {
    deadline = Date.now() + DEADLINE_MS;

    apply();
    if (settled()) {
      return;
    }

    // BV injects its JSON-LD asynchronously, into <head>, after the widget reports ready.
    observer = new MutationObserver(scheduleApply);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-bv-ready']
    });
  }

  // Script is deferred, so the DOM is already parsed when this runs.
  init();
})();
